/**
 * OverlayBridge — single source of truth for syncing KLineChart overlay
 * objects with the Zustand drawing store.
 *
 * Architecture:
 *   ChartArea calls bridge.init(chart) on mount.
 *   bridge.createDrawing() creates a real chart overlay with per-overlay
 *   event callbacks (onDrawEnd, onSelected, onDeselected, onRemoved).
 *   These callbacks are the ONLY thing that mutates the drawing store, so
 *   the store always reflects the actual chart state.
 *
 * External callers (ToolsDrawer, keyboard shortcuts) go through the bridge
 * — never touch the chart directly for overlay CRUD.
 */

import type { Chart as KChart } from "klinecharts";
import { useDrawingStore, type DrawingToolId, type DrawingRecord } from "@/stores/drawingStore";
import type { SerializedDrawing, SerializedOverlayPoint } from "@/stores/sessionStore";
import { DRAW_COLORS } from "@/styles/tokens";
import {
  canRedo,
  canUndo,
  clearHistory,
  pushHistory,
  redo,
  undo,
  withHistorySuppressed,
  type DrawingHistoryEntry,
} from "@/lib/lytick/drawingHistory";
import {
  TOOL_TO_OVERLAY as CANONICAL_TOOL_TO_OVERLAY,
  TOOL_LABELS as CANONICAL_TOOL_LABELS,
} from "@/lib/lytick/drawingToolDefinitions";
import { findNearestOHLC } from "@/lib/lytick/snapMode";

// ─── reverse maps (derived from canonical definitions) ──────────────────────────

const TOOL_TO_OVERLAY = CANONICAL_TOOL_TO_OVERLAY as Record<string, string>;

/**
 * NOTE on 'ray' and 'priceLine' overlays:
 *   - ray → 'rayLine': KLineChart native overlay (draws a ray from a point).
 *   - priceLine → 'priceLine': KLineChart native overlay (horizontal price line).
 * Both are built-in KLineChart overlays, confirmed in TOOL_TO_OVERLAY mapping.
 */

const OVERLAY_TO_TOOL: Record<string, string> = {};
for (const [tid, oname] of Object.entries(TOOL_TO_OVERLAY)) {
  OVERLAY_TO_TOOL[oname] = tid;
}

/**
 * Resolve the tool id for an overlay. The text box and tag tools share the
 * single "text" overlay, so the variant stored in extendData disambiguates them.
 */
function resolveToolId(overlay: any): string | undefined {
  if (overlay?.name === "text") {
    return (overlay?.extendData as any)?.variant === "tag" ? "textTag" : "textAnnotation";
  }
  return OVERLAY_TO_TOOL[overlay?.name];
}

const TOOL_LABELS = CANONICAL_TOOL_LABELS;

// ─── module state ────────────────────────────────────────────────────────────

let chartInstance: KChart | null = null;
let isBulkClearing = false;
/** Overlay IDs that have been created but not yet completed (multi-step). */
const pendingIds = new Set<string>();
/** Callback fired when a drawing is right-clicked (desktop context menu). */
let drawingRightClickCb: ((drawingId: string, x: number, y: number) => void) | null = null;

export interface DrawingSnapshot extends SerializedDrawing {
  id?: string;
  lineWidth?: number;
  lineStyle?: "solid" | "dashed";
  opacity?: number;
  name?: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Lazily import the drawing store to avoid circular deps at module level. */
function getStore() {
  return useDrawingStore.getState();
}

function getChart() {
  return chartInstance;
}

function getOverlayById(id: string): any | null {
  const chart = getChart();
  if (!chart) {
    return null;
  }

  try {
    const overlays = chart.getOverlays();
    return overlays.find((overlay: any) => overlay.id === id) ?? null;
  } catch {
    return null;
  }
}

function getLineStyle(style: unknown): "solid" | "dashed" {
  return style === "dashed" ? "dashed" : "solid";
}

function normalizeOpacity(opacity: unknown): number {
  const value = Number(opacity);
  if (!Number.isFinite(value)) {
    return 100;
  }

  return Math.max(10, Math.min(100, Math.round(value)));
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function parseHexColor(hex: string) {
  const normalized = hex.replace("#", "").trim();
  if (![3, 4, 6, 8].includes(normalized.length)) return null;

  const expanded =
    normalized.length <= 4
      ? normalized
          .split("")
          .map((part) => `${part}${part}`)
          .join("")
      : normalized;

  const hasAlpha = expanded.length === 8;
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  const a = hasAlpha ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1;

  if ([r, g, b, a].some((part) => !Number.isFinite(part))) return null;
  return { r, g, b, a };
}

function parseRgbColor(color: string) {
  const normalized = color.replace(/\s+/g, "");
  const match = normalized.match(
    /^rgba?\(([\d.]+),([\d.]+),([\d.]+)(?:,([\d.]+))?\)$/i,
  );
  if (!match) return null;

  const r = Number(match[1]);
  const g = Number(match[2]);
  const b = Number(match[3]);
  const a = match[4] == null ? 1 : Number(match[4]);

  if ([r, g, b, a].some((part) => !Number.isFinite(part))) return null;
  return { r, g, b, a };
}

function parseColor(color: unknown) {
  if (typeof color !== "string" || color.trim().length === 0) return null;
  if (color.trim().startsWith("#")) return parseHexColor(color.trim());
  if (color.trim().startsWith("rgb")) return parseRgbColor(color.trim());
  return null;
}

function formatHex(color: { r: number; g: number; b: number }) {
  const toHex = (value: number) =>
    clampNumber(Math.round(value), 0, 255).toString(16).padStart(2, "0");
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`.toUpperCase();
}

function withAlpha(color: string, opacity: number) {
  const parsed = parseColor(color);
  if (!parsed) return color;
  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${clampNumber(opacity, 0, 1)})`;
}

function resolveOverlayPrimaryColor(overlay: any): string {
  return (
    (typeof overlay?.styles?.line?.color === "string" && overlay.styles.line.color) ||
    (typeof overlay?.styles?.rect?.borderColor === "string" &&
      overlay.styles.rect.borderColor) ||
    (typeof overlay?.styles?.polygon?.borderColor === "string" &&
      overlay.styles.polygon.borderColor) ||
    (typeof overlay?.styles?.text?.borderColor === "string" &&
      overlay.styles.text.borderColor) ||
    DRAW_COLORS.bullish
  );
}

function resolveOverlayOpacityPercent(overlay: any): number {
  const explicitOpacity = Number(overlay?.styles?.line?.opacity);
  if (Number.isFinite(explicitOpacity)) {
    return normalizeOpacity(explicitOpacity);
  }

  const parsed =
    parseColor(overlay?.styles?.line?.color) ??
    parseColor(overlay?.styles?.rect?.color) ??
    parseColor(overlay?.styles?.polygon?.color) ??
    parseColor(overlay?.styles?.text?.backgroundColor);

  return normalizeOpacity((parsed?.a ?? 1) * 100);
}

function resolveOverlayBaseColor(overlay: any): string {
  const primary = resolveOverlayPrimaryColor(overlay);
  const parsed = parseColor(primary);
  return parsed ? formatHex(parsed) : primary;
}

function buildOverlayStyles(
  overlayName: string,
  existingStyles: any,
  color: string,
  lineWidth: number,
  lineStyle: "solid" | "dashed",
  opacity: number,
) {
  const opacityRatio = clampNumber(opacity / 100, 0.12, 1);
  const lineColor = withAlpha(color, opacityRatio);
  const fillColor = withAlpha(color, clampNumber(opacityRatio * 0.22, 0.08, 0.5));
  const activePointColor = withAlpha(color, clampNumber(opacityRatio * 0.3, 0.18, 0.4));

  if (overlayName === "riskReward") {
    return existingStyles ?? {};
  }

  return {
    ...(existingStyles ?? {}),
    line: {
      ...(existingStyles?.line ?? {}),
      color: lineColor,
      size: lineWidth,
      style: lineStyle,
    },
    point: {
      ...(existingStyles?.point ?? {}),
      borderColor: lineColor,
      activeBorderColor: lineColor,
      activeColor: activePointColor,
    },
    rect: {
      ...(existingStyles?.rect ?? {}),
      borderColor: lineColor,
      color: fillColor,
    },
    polygon: {
      ...(existingStyles?.polygon ?? {}),
      borderColor: lineColor,
      color: fillColor,
    },
    circle: {
      ...(existingStyles?.circle ?? {}),
      borderColor: lineColor,
      color: fillColor,
    },
    arc: {
      ...(existingStyles?.arc ?? {}),
      color: lineColor,
    },
    text: {
      ...(existingStyles?.text ?? {}),
      borderColor: lineColor,
      backgroundColor: fillColor,
    },
  };
}

function extractDrawingSnapshot(id: string): DrawingSnapshot | null {
  const overlay = getOverlayById(id);
  if (!overlay) {
    return null;
  }

  const toolId = resolveToolId(overlay) ?? "trendLine";
  const lineStyles = overlay.styles?.line ?? {};
  const serializedPoints: SerializedOverlayPoint[] = Array.isArray(overlay.points)
    ? overlay.points
        .map((point: any) => ({
          timestamp: Number(point?.timestamp),
          value: Number(point?.value),
          dataIndex:
            typeof point?.dataIndex === "number" ? Number(point.dataIndex) : undefined,
        }))
        .filter(
          (point) =>
            Number.isFinite(point.timestamp) && Number.isFinite(point.value),
        )
    : [];

  return {
    id,
    name: overlay.name,
    toolId,
    color: resolveOverlayBaseColor(overlay),
    points: serializedPoints,
    extendData: overlay.extendData,
    visible: overlay.visible !== false,
    locked: Boolean(overlay.lock),
    lineWidth:
      typeof lineStyles.size === "number" && Number.isFinite(lineStyles.size)
        ? lineStyles.size
        : 2,
    lineStyle: getLineStyle(lineStyles.style),
    opacity: resolveOverlayOpacityPercent(overlay),
  };
}

function makeHistoryEntry(
  type: DrawingHistoryEntry<DrawingSnapshot>["type"],
  drawingId: string,
  before: DrawingSnapshot | null,
  after: DrawingSnapshot | null,
) {
  pushHistory<DrawingSnapshot>({
    type,
    drawingId,
    before,
    after,
    timestamp: Date.now(),
  });
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Initialise the bridge with a live chart instance.
 * Call once on chart mount. Call `destroy()` on unmount.
 */
export function initOverlayBridge(chart: KChart): void {
  chartInstance = chart;
  clearHistory();
  // Initial sync — in case overlays already exist (shouldn't, but safe)
  syncFromChart();
}

/**
 * Tear down the bridge.
 */
export function destroyOverlayBridge(): void {
  pendingIds.clear();
  chartInstance = null;
}

/**
 * Create a drawing overlay on the chart.
 *
 * - The overlay is created with event callbacks that keep the store in sync.
 * - Returns the overlay ID (string) or null on failure.
 * - The ID is also added to the pending set until onDrawEnd fires.
 */
export function createDrawing(
  toolId: string,
  color: string,
): string | null {
  const chart = chartInstance;
  if (!chart) return null;

  const overlayName = TOOL_TO_OVERLAY[toolId];
  if (!overlayName) return null;

  const store = getStore();
  // Check for a saved template first, fall back to tool defaults
  const template = store.loadTemplate(toolId as DrawingToolId);
  const defaults = template ?? store.toolDefaults[toolId as DrawingToolId] ?? {
    color,
    lineWidth: 2,
    lineStyle: "solid",
    opacity: 100,
  };
  const effectiveColor = color ?? defaults.color;
  const extendData =
    (toolId === "textAnnotation" || toolId === "textTag")
      ? {
          text: "",
          variant: toolId === "textTag" ? "tag" : "box",
          size: "M",
          color: toolId === "textTag" ? "#94a3b8" : effectiveColor,
          bold: false,
          italic: false,
          showBackground: true,
          background: toolId === "textTag" ? "rgba(148, 163, 184, 0.25)" : "rgba(30, 41, 59, 0.5)",
        }
      : undefined;

  const id = chart.createOverlay({
    name: overlayName,
    styles: buildOverlayStyles(
      overlayName,
      undefined,
      effectiveColor,
      defaults.lineWidth,
      defaults.lineStyle,
      defaults.opacity,
    ),
    extendData,
    // --- event callbacks that sync store ---
    onDrawEnd: (e) => {
      // Drawing completed — register in store
      pendingIds.delete(e.overlay.id);

      // Snap all placed points to nearest OHLC when magnet mode is active
      if (chart && getStore().snapMode && e.overlay.points?.length) {
        const snappedPoints = e.overlay.points.map((pt: any) => {
          if (pt.timestamp == null || pt.value == null) return pt;
          const snapped = findNearestOHLC(pt.timestamp, pt.value, chart);
          return snapped ? { ...pt, value: snapped.value } : pt;
        });
        chart.overrideOverlay({ id: e.overlay.id, points: snappedPoints });
      }

      const s = getStore();
      // Avoid duplicates (drawEnd can fire on re-draw)
      if (!s.drawings.find((d) => d.id === e.overlay.id)) {
        s.addDrawing({
          id: e.overlay.id,
          toolId,
          label: TOOL_LABELS[toolId] ?? toolId,
          color: effectiveColor,
          visible: true,
          locked: false,
          createdAt: Date.now(),
          lineWidth: defaults.lineWidth,
          lineStyle: defaults.lineStyle,
          opacity: defaults.opacity,
        });
      }
      const snapshot = extractDrawingSnapshot(e.overlay.id);
      if (snapshot) {
        makeHistoryEntry("create", e.overlay.id, null, snapshot);
      }
      s.setActiveTool(null);
      s.setSelectedDrawingIds([e.overlay.id]);
      if (toolId === "textAnnotation" || toolId === "textTag") {
        s.setEditingTextDrawingId(e.overlay.id);
      }
    },
    onSelected: (e) => {
      getStore().setSelectedDrawingIds([e.overlay.id]);
    },
    onDeselected: () => {
      getStore().clearDrawingSelection();
    },
    onRemoved: (e) => {
      if (isBulkClearing) return; // handled by clearAllDrawings
      pendingIds.delete(e.overlay.id);
      getStore().removeDrawing(e.overlay.id);
    },
    onRightClick: (e) => {
      // Select drawing instead of dismissing it natively
      e?.preventDefault?.();
      getStore().setSelectedDrawingIds([e.overlay.id]);
      // Notify context menu handler (desktop right-click menu)
      drawingRightClickCb?.(e.overlay.id, e.pageX ?? 0, e.pageY ?? 0);
      return true;
    },
  });

  if (typeof id === "string") {
    pendingIds.add(id);
    return id;
  }
  return null;
}

/**
 * Remove a single drawing from the chart.
 * The onRemoved callback will clean up the store entry.
 */
export function removeDrawing(id: string): void {
  const chart = chartInstance;
  if (!chart) return;
  const snapshot = extractDrawingSnapshot(id);
  if (snapshot) {
    makeHistoryEntry("delete", id, snapshot, null);
  }
  pendingIds.delete(id);
  chart.removeOverlay({ id });
  // Safety: also remove from store in case onRemoved doesn't fire
  getStore().removeDrawing(id);
}

/**
 * Remove ALL drawings from the chart.
 * Uses a bulk flag so individual onRemoved callbacks don't cause N re-renders.
 */
export function clearAllDrawings(): void {
  const chart = chartInstance;
  if (!chart) return;
  withHistorySuppressed(() => {
    isBulkClearing = true;
    pendingIds.clear();
    chart.removeOverlay({});
    isBulkClearing = false;
    // Single bulk store clear
    getStore().clearDrawings();
  });
}

/**
 * Toggle visibility of a drawing.
 */
export function setDrawingVisible(id: string, visible: boolean): void {
  const chart = chartInstance;
  if (!chart) return;
  chart.overrideOverlay({ id, visible });
  getStore().updateDrawing(id, { visible });
}

/**
 * Toggle locked state of a drawing.
 */
export function setDrawingLocked(id: string, locked: boolean): void {
  const chart = chartInstance;
  if (!chart) return;
  chart.overrideOverlay({ id, lock: locked });
  getStore().updateDrawing(id, { locked });
}

export function getDrawingSnapshot(id: string): DrawingSnapshot | null {
  return extractDrawingSnapshot(id);
}

export function getDrawingViewportBounds(id: string): {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
} | null {
  const chart = getChart();
  const overlay = getOverlayById(id);
  if (!chart || !overlay || !Array.isArray(overlay.points) || overlay.points.length === 0) {
    return null;
  }

  try {
    const rawCoordinates = chart.convertToPixel(overlay.points, { paneId: overlay.paneId });
    const coordinates = Array.isArray(rawCoordinates) ? rawCoordinates : [rawCoordinates];
    const validCoordinates = coordinates.filter(
      (coordinate: any) =>
        Number.isFinite(Number(coordinate?.x)) && Number.isFinite(Number(coordinate?.y)),
    );

    if (validCoordinates.length === 0) {
      return null;
    }

    const xs = validCoordinates.map((coordinate: any) => Number(coordinate.x));
    const ys = validCoordinates.map((coordinate: any) => Number(coordinate.y));
    const left = Math.min(...xs);
    const right = Math.max(...xs);
    const top = Math.min(...ys);
    const bottom = Math.max(...ys);

    return {
      left,
      right,
      top,
      bottom,
      centerX: left + (right - left) / 2,
      centerY: top + (bottom - top) / 2,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
    };
  } catch {
    return null;
  }
}

export function setDrawingProperties(
  drawingId: string,
  properties: {
    color?: string;
    lineWidth?: number;
    lineStyle?: "solid" | "dashed";
    opacity?: number;
  },
): void {
  const chart = getChart();
  if (!chart) {
    return;
  }

  const before = extractDrawingSnapshot(drawingId);
  if (!before) {
    return;
  }

  const overlay = getOverlayById(drawingId);
  if (!overlay) {
    return;
  }

  chart.overrideOverlay({
    id: drawingId,
    styles: buildOverlayStyles(
      overlay.name,
      overlay.styles,
      properties.color ?? before.color,
      properties.lineWidth ?? before.lineWidth ?? 2,
      properties.lineStyle ?? before.lineStyle ?? "solid",
      properties.opacity ?? before.opacity ?? 100,
    ),
  });

  getStore().updateDrawing(drawingId, {
    color: properties.color ?? before.color,
    lineWidth: properties.lineWidth ?? before.lineWidth ?? 2,
    lineStyle: properties.lineStyle ?? before.lineStyle ?? "solid",
    opacity: properties.opacity ?? before.opacity ?? 100,
  });
  syncFromChart();
  const after = extractDrawingSnapshot(drawingId);
  if (after) {
    makeHistoryEntry("update", drawingId, before, after);
  }
}

export function setDrawingExtendData(drawingId: string, extendData: unknown): void {
  const chart = getChart();
  if (!chart) {
    return;
  }

  const before = extractDrawingSnapshot(drawingId);
  if (!before) {
    return;
  }

  chart.overrideOverlay({
    id: drawingId,
    extendData,
  });

  syncFromChart();
  const after = extractDrawingSnapshot(drawingId);
  if (after) {
    makeHistoryEntry("update", drawingId, before, after);
  }
}

export function duplicateDrawing(drawingId: string): string | null {
  const chart = getChart();
  const snapshot = extractDrawingSnapshot(drawingId);
  if (!chart || !snapshot) {
    return null;
  }

  const nextPoints = snapshot.points.map((point, index) => ({
    timestamp: point.timestamp + 60_000 * (index + 1),
    value: point.value * 1.001,
    dataIndex:
      typeof point.dataIndex === "number" ? point.dataIndex + 2 : undefined,
  }));

  const nextId = chart.createOverlay({
    name: TOOL_TO_OVERLAY[snapshot.toolId],
    points: nextPoints,
    styles: buildOverlayStyles(
      TOOL_TO_OVERLAY[snapshot.toolId],
      undefined,
      snapshot.color,
      snapshot.lineWidth ?? 2,
      snapshot.lineStyle ?? "solid",
      snapshot.opacity ?? 100,
    ),
    extendData: snapshot.extendData,
    visible: snapshot.visible,
    lock: snapshot.locked,
    onRightClick: (e) => {
      e?.preventDefault?.();
      getStore().setSelectedDrawingIds([e.overlay.id]);
      return true;
    },
  });

  syncFromChart();
  return typeof nextId === "string" ? nextId : null;
}

function restoreDrawingFromSnapshot(snapshot: DrawingSnapshot | null): string | null {
  const chart = getChart();
  if (!chart || !snapshot) {
    return null;
  }

  const overlayName = TOOL_TO_OVERLAY[snapshot.toolId];
  if (!overlayName) {
    return null;
  }

  const id = chart.createOverlay({
    name: overlayName,
    points: snapshot.points.map((point) => ({
      timestamp: point.timestamp,
      value: point.value,
      dataIndex: point.dataIndex,
    })),
    styles: buildOverlayStyles(
      overlayName,
      undefined,
      snapshot.color,
      snapshot.lineWidth ?? 2,
      snapshot.lineStyle ?? "solid",
      snapshot.opacity ?? 100,
    ),
    extendData: snapshot.extendData,
    visible: snapshot.visible,
    lock: snapshot.locked,
    onRightClick: (e) => {
      e?.preventDefault?.();
      getStore().setSelectedDrawingIds([e.overlay.id]);
      return true;
    },
  });

  syncFromChart();
  return typeof id === "string" ? id : null;
}

export function undoLastDrawingAction() {
  const entry = undo<DrawingSnapshot>();
  if (!entry) {
    return null;
  }

  return withHistorySuppressed(() => {
    if (entry.type === "create") {
      removeDrawing(entry.drawingId);
      return entry;
    }

    if (entry.type === "delete") {
      restoreDrawingFromSnapshot(entry.before ?? null);
      return entry;
    }

    if (entry.type === "update" && entry.before) {
      if (entry.before.id && getOverlayById(entry.before.id)) {
        setDrawingProperties(entry.before.id, {
          color: entry.before.color,
          lineWidth: entry.before.lineWidth,
          lineStyle: entry.before.lineStyle,
          opacity: entry.before.opacity,
        });
        if (entry.before.extendData !== undefined) {
          setDrawingExtendData(entry.before.id, entry.before.extendData);
        }
      }
      return entry;
    }

    return entry;
  });
}

export function redoLastDrawingAction() {
  const entry = redo<DrawingSnapshot>();
  if (!entry) {
    return null;
  }

  return withHistorySuppressed(() => {
    if (entry.type === "create") {
      restoreDrawingFromSnapshot(entry.after ?? null);
      return entry;
    }

    if (entry.type === "delete") {
      removeDrawing(entry.drawingId);
      return entry;
    }

    if (entry.type === "update" && entry.after) {
      if (entry.after.id && getOverlayById(entry.after.id)) {
        setDrawingProperties(entry.after.id, {
          color: entry.after.color,
          lineWidth: entry.after.lineWidth,
          lineStyle: entry.after.lineStyle,
          opacity: entry.after.opacity,
        });
        if (entry.after.extendData !== undefined) {
          setDrawingExtendData(entry.after.id, entry.after.extendData);
        }
      }
      return entry;
    }

    return entry;
  });
}

export function canUndoDrawingAction() {
  return canUndo();
}

export function canRedoDrawingAction() {
  return canRedo();
}

/**
 * Register a callback that fires when a drawing overlay is right-clicked.
 * Used by DrawingContextMenu for the desktop context menu.
 * Returns a cleanup function.
 */
export function setDrawingRightClickHandler(
  handler: ((drawingId: string, x: number, y: number) => void) | null,
): void {
  drawingRightClickCb = handler;
}

/**
 * Cancel any in-progress (pending) drawing overlay.
 * Call when the user presses Escape while a multi-step tool is active.
 */
export function cancelPendingDrawings(): void {
  const chart = chartInstance;
  if (!chart) return;
  for (const id of pendingIds) {
    try {
      chart.removeOverlay({ id });
    } catch {
      // may already be removed
    }
  }
  pendingIds.clear();
}

/**
 * Sync store state from the actual chart overlays.
 * Used on init and as a safety net.
 *
 * - Removes store entries whose overlays no longer exist on the chart.
 * - Adds store entries for overlays on the chart that aren't tracked.
 * - Updates visible/locked flags from chart state.
 */
export function syncFromChart(): void {
  const chart = chartInstance;
  if (!chart) return;

  const store = getStore();
  let overlays: any[];
  try {
    overlays = chart.getOverlays();
  } catch {
    return;
  }

  const chartIds = new Set<string>();
  const drawingMap = new Map(store.drawings.map((d) => [d.id, d]));
  const addedIds = new Set<string>();

  for (const ov of overlays) {
    chartIds.add(ov.id);

    const existing = drawingMap.get(ov.id);
    if (existing) {
      // Sync mutable properties from chart
      if (
        existing.visible !== ov.visible ||
        existing.locked !== ov.lock ||
        existing.color !== resolveOverlayBaseColor(ov) ||
        existing.lineWidth !== ov.styles?.line?.size ||
        existing.lineStyle !== getLineStyle(ov.styles?.line?.style) ||
        existing.opacity !== resolveOverlayOpacityPercent(ov)
      ) {
        store.updateDrawing(ov.id, {
          visible: ov.visible,
          locked: ov.lock,
          color: resolveOverlayBaseColor(ov),
          lineWidth:
            typeof ov.styles?.line?.size === "number"
              ? ov.styles.line.size
              : existing.lineWidth,
          lineStyle: getLineStyle(ov.styles?.line?.style),
          opacity: resolveOverlayOpacityPercent(ov),
        });
      }
    } else if (!addedIds.has(ov.id)) {
      // Overlay exists on chart but not in store (external creation / missed event)
      const toolId = resolveToolId(ov);
      if (toolId) {
        addedIds.add(ov.id);
        store.addDrawing({
          id: ov.id,
          toolId,
          label: TOOL_LABELS[toolId] ?? toolId,
          color: resolveOverlayBaseColor(ov),
          visible: ov.visible ?? true,
          locked: ov.lock ?? false,
          createdAt: Date.now(),
          lineWidth:
            typeof ov.styles?.line?.size === "number" ? ov.styles.line.size : 2,
          lineStyle: getLineStyle(ov.styles?.line?.style),
          opacity: resolveOverlayOpacityPercent(ov),
        });
      }
    }
  }

  // Remove store entries whose overlays no longer exist
  for (const d of store.drawings) {
    if (!chartIds.has(d.id)) {
      store.removeDrawing(d.id);
    }
  }
}

// ─── Serialization (Session Restore) ────────────────────────────────────

/**
 * Serialize all LYTICK drawings from the chart into storable snapshots.
 * Called during session save — extracts points, color, visibility, lock state.
 * Only serializes drawings that have completed points (ignores pending/incomplete).
 *
 * Inspired by reference chartCache.js normalizeOverlaySnapshots — validates
 * and normalizes every field to prevent corrupted data in storage.
 */
export function serializeOverlays(): SerializedDrawing[] {
  const chart = chartInstance;
  if (!chart) return [];

  let overlays: any[];
  try {
    overlays = chart.getOverlays();
  } catch {
    return [];
  }

  const result: SerializedDrawing[] = [];

  for (const ov of overlays) {
    // Must have a recognized overlay name
    if (!ov || typeof ov.name !== "string") continue;
    const toolId = resolveToolId(ov);
    if (!toolId) continue;

    // Validate and normalize points — skip overlays with no usable points
    const rawPoints = Array.isArray(ov.points) ? ov.points : [];
    const points: SerializedOverlayPoint[] = [];
    for (const p of rawPoints) {
      const timestamp = typeof p?.timestamp === "number" && Number.isFinite(p.timestamp) ? p.timestamp : undefined;
      const value = typeof p?.value === "number" && Number.isFinite(p.value) ? p.value : undefined;
      if (timestamp !== undefined && value !== undefined) {
        points.push({
          timestamp,
          value,
          dataIndex: typeof p?.dataIndex === "number" && Number.isFinite(p.dataIndex) ? p.dataIndex : undefined,
        });
      }
    }
    if (points.length === 0) continue;

    result.push({
      toolId,
      color: resolveOverlayBaseColor(ov),
      points,
      extendData: ov.extendData ?? undefined,
      visible: ov.visible !== false,
      locked: Boolean(ov.lock),
    });
  }

  return result;
}

/**
 * Restore serialized drawings onto the chart.
 * Called after chart init + data load — recreates overlays from saved snapshots.
 *
 * Returns the count of successfully restored drawings.
 *
 * Limitations:
 *   - Drawings are restored with their saved points, but these map to absolute
 *     timestamps. If the symbol/timeframe changed, points may land at incorrect
 *     chart positions. This is acceptable because the session also restores the
 *     same symbol+timeframe, so data should match.
 *   - Custom fibonacci extendData (levels config) IS preserved.
 *   - Drawing IDs will be new (KLineCharts generates fresh IDs on createOverlay),
 *     which is fine — the store is populated from onDrawEnd or syncFromChart.
 */
export function deserializeOverlays(drawings: SerializedDrawing[]): number {
  const chart = chartInstance;
  if (!chart || !Array.isArray(drawings) || drawings.length === 0) return 0;

  isBulkClearing = true;
  let restored = 0;
  const store = getStore();
  const existingIds = new Set(store.drawings.map((d) => d.id));

  for (const d of drawings) {
    if (!d || typeof d !== "object" || typeof d.toolId !== "string") continue;
    const overlayName = TOOL_TO_OVERLAY[d.toolId as DrawingToolId];
    if (!overlayName) continue;

    // Validate points array
    const rawPoints = Array.isArray(d.points) ? d.points : [];
    const validPoints = rawPoints.filter(
      (p) =>
        typeof p?.timestamp === "number" && Number.isFinite(p.timestamp) &&
        typeof p?.value === "number" && Number.isFinite(p.value),
    );
    if (validPoints.length === 0) continue;

    try {
      const id = chart.createOverlay({
        name: overlayName,
        points: validPoints.map((p) => ({
          timestamp: p.timestamp,
          value: p.value,
          dataIndex: typeof p.dataIndex === "number" && Number.isFinite(p.dataIndex) ? p.dataIndex : undefined,
        })),
        styles: buildOverlayStyles(
          overlayName,
          undefined,
          typeof d.color === "string" ? d.color : DRAW_COLORS.bullish,
          2,
          "solid",
          100,
        ),
        extendData: d.extendData ?? undefined,
        visible: d.visible !== false,
        lock: Boolean(d.locked),
      });

      if (typeof id === "string") {
        // Register in drawing store (skip onDrawEnd, these are pre-completed)
        if (!existingIds.has(id)) {
          existingIds.add(id);
          store.addDrawing({
            id,
            toolId: d.toolId,
            label: TOOL_LABELS[d.toolId] ?? d.toolId,
            color: d.color,
            visible: d.visible,
            locked: d.locked,
            createdAt: Date.now(),
          });
        }
        restored++;
      }
    } catch {
      // Skip any drawing that fails to reconstruct
    }
  }

  isBulkClearing = false;
  return restored;
}
